/**
 * BondPowersSection - Dynamic per-power bond entries for AcceptQuoteModal
 */

import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { OathInput } from '../../../../shared/OathUI';
import { ACCEPT_QUOTE_FIELD_MAX_LENGTH } from '../../shared/requestFieldLimits';
import { formatCurrencyInput, unformatCurrencyInput } from '../../shared/formatUtils';

const formatCurrency = (val) =>
  val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const BondPowersSection = ({ powers, onUpdatePower, requiredFields, powerTotal = 0, totalBondAmount = 0, amountsMatch = false }) => {
  if (!powers || powers.length === 0) return null;

  const requiresOffenseType = requiredFields.includes('chargeType');
  const requiresCourtDate = requiredFields.includes('courtDateFound');
  const hasTotalEntered = totalBondAmount > 0;
  const hasPowerAmounts = powerTotal > 0;

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Bond Information</h4>
      <div className="space-y-4">
        {powers.map((power) => (
          <div
            key={power.id}
            className="border border-gray-200 dark:border-gray-700 rounded-xl p-4"
          >
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Power #{power.id + 1}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <OathInput
                label="Bond Amount"
                value={formatCurrencyInput(power.bondAmount?.toString() || '')}
                onChange={(val) => onUpdatePower(power.id, 'bondAmount', unformatCurrencyInput(val).replace(/[^0-9.]/g, ''))}
                placeholder="0.00"
                prefix="$"
                required
              />
              <OathInput
                label="Offense"
                maxLength={ACCEPT_QUOTE_FIELD_MAX_LENGTH.offense}
                value={power.offense || ''}
                onChange={(val) => onUpdatePower(power.id, 'offense', val)}
                placeholder="Offense description"
                required
              />
              {requiresOffenseType && (
                <OathInput
                  label="Offense Type"
                  maxLength={ACCEPT_QUOTE_FIELD_MAX_LENGTH.offenseType}
                  value={power.offenseType || ''}
                  onChange={(val) => onUpdatePower(power.id, 'offenseType', val)}
                  placeholder="e.g., Felony, Misdemeanor"
                  required
                />
              )}
              <OathInput
                label="Case Number"
                maxLength={ACCEPT_QUOTE_FIELD_MAX_LENGTH.caseNumber}
                value={power.caseNumber || ''}
                onChange={(val) => onUpdatePower(power.id, 'caseNumber', val)}
                placeholder="Case #"
              />
              <OathInput
                label="Courthouse"
                maxLength={ACCEPT_QUOTE_FIELD_MAX_LENGTH.courtPlace}
                value={power.courtPlace || ''}
                onChange={(val) => onUpdatePower(power.id, 'courtPlace', val.substring(0, 80))}
                placeholder="Courthouse name"
              />
              {requiresCourtDate && (
                <OathInput
                  label="Court Date"
                  type="date"
                  value={power.courtDate || ''}
                  onChange={(val) => onUpdatePower(power.id, 'courtDate', val)}
                  required
                />
              )}
            </div>
          </div>
        ))}

        {/* Running total indicator */}
        {hasTotalEntered && (
          <div
            className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
              amountsMatch
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
            }`}
          >
            {amountsMatch ? (
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            )}
            <span>
              Power Total: <span className="font-bold">{formatCurrency(powerTotal)}</span>
              {' / '}
              Total Bond Amount: <span className="font-bold">{formatCurrency(totalBondAmount)}</span>
              {amountsMatch
                ? ' — Amounts match'
                : hasPowerAmounts
                  ? ` — ${powerTotal < totalBondAmount ? 'Under' : 'Over'} by ${formatCurrency(Math.abs(totalBondAmount - powerTotal))}`
                  : ' — Enter bond amounts above'
              }
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BondPowersSection;
